import { useMemo } from "react";

import { useLocalization } from "@/hooks";
import { Question } from "@/types/resources";
import { QuestionType, RuleOperator } from "@/types/utils";

/**
 * Question types whose answer a skip-logic rule can branch on: single choice
 * and category assignment expose one selected option (eq / neq), multiple
 * choice exposes a set the rule tests membership against (in / notin).
 */
export const CONDITION_SOURCE_TYPES: QuestionType[] = [
  "category_assignment",
  "multiple_choice",
  "single_choice",
];

/**
 * Prior questions (before `currentQuestionOrder`) usable as a rule's
 * cross-question source, ordered by position.
 */
export const conditionSourceQuestions = (
  allQuestions: Question[],
  currentQuestionOrder: number | undefined
): Question[] =>
  currentQuestionOrder === undefined
    ? []
    : allQuestions
        .filter(
          (q) =>
            q.order < currentQuestionOrder &&
            CONDITION_SOURCE_TYPES.includes(q.type)
        )
        .sort((a, b) => a.order - b.order);

/** Operators valid for a given question type. */
export const operatorsForQuestionType = (type: QuestionType): RuleOperator[] =>
  type === "multiple_choice"
    ? [RuleOperator.In, RuleOperator.NotIn]
    : [RuleOperator.Equal, RuleOperator.NotEqual];

/** The operator a new rule on this question type defaults to. */
export const defaultOperatorForQuestionType = (
  type: QuestionType
): RuleOperator => operatorsForQuestionType(type)[0];

/**
 * Short, translated label for each operator ("is", "is not", "contains",
 * "does not contain"), keyed by operator. Every `t()` call is a plain string
 * literal so the translation scanner can see them.
 */
export const useOperatorLabels = (): Record<RuleOperator, string> => {
  const { i18n } = useLocalization();
  return useMemo(
    () => ({
      [RuleOperator.Equal]: i18n.t("questions.is"),
      [RuleOperator.NotEqual]: i18n.t("questions.isNot"),
      [RuleOperator.In]: i18n.t("questions.contains"),
      [RuleOperator.NotIn]: i18n.t("questions.doesNotContain"),
    }),
    [i18n]
  );
};
